import React, { useState, useEffect } from "react";
import { useTheme, THEME_PRESETS } from "../../../contexts/ThemeContext";
import ColorPickerInput from "../../../components/ColorPickerInput";
import "./AppearanceSettings.css";

const ToggleSwitch = ({ checked, onChange }) => {
  const handleToggle = () => {
    console.log("🔄 Toggle acionado - atual:", checked, "→ novo:", !checked);
    onChange(!checked);
  };

  return (
    <div 
      className={`toggle-switch ${checked ? "active" : ""}`} 
      onClick={handleToggle}
      role="button"
      tabIndex={0}
      aria-pressed={checked}
      onKeyDown={(e) => e.key === 'Enter' && handleToggle()}
    >
      <div className="toggle-switch-slider"></div>
    </div>
  );
};

const AppearanceSettings = ({ settings, onUpdate }) => {
  const [localSettings, setLocalSettings] = useState(settings || {});
  const { 
    darkMode, 
    colors, 
    updateColor, 
    applyPreset, 
    resetColors, 
    toggleDarkMode 
  } = useTheme();

  useEffect(() => {
    setLocalSettings(settings || {});
  }, [settings]);

  const handleUpdate = async (key, value) => {
    const updated = { ...localSettings, [key]: value };
    setLocalSettings(updated);
    try {
      await onUpdate(updated);
    } catch (error) {
      setLocalSettings(localSettings);
    }
  };

  const handleColorChange = (colorKey, value) => {
    updateColor(colorKey, value);
  };

  const handlePresetApply = (presetKey) => {
    applyPreset(presetKey);
  };

  const handleResetColors = () => {
    if (window.confirm("Deseja restaurar as cores padrão?")) {
      resetColors();
    }
  };

  return (
    <div>
      <div className="settings-header">
        <h1>Aparência</h1>
        <p>Personalize a aparência da plataforma</p>
      </div>

      {/* Seção de Modo Claro/Escuro */}
      <div className="settings-section">
        <h3 className="settings-section-title">🌓 Modo Claro/Escuro</h3>
        
        <div className="settings-item">
          <div className="settings-item-info">
            <div className="settings-item-label">Modo Escuro</div>
            <div className="settings-item-description">Ativar tema escuro para reduzir o cansaço visual</div>
          </div>
          <div className="settings-item-control">
            <ToggleSwitch
              checked={darkMode}
              onChange={toggleDarkMode}
            />
          </div>
        </div>
      </div>

      {/* Seção de Temas Predefinidos */}
      <div className="settings-section">
        <h3 className="settings-section-title">🎨 Temas Predefinidos</h3>
        <p className="settings-section-description">Escolha um tema pré-configurado ou customize suas próprias cores</p>
        
        <div className="theme-presets-grid">
          {Object.entries(THEME_PRESETS).map(([key, preset]) => (
            <div key={key} className="theme-preset-card">
              <div className="theme-preset-colors">
                {Object.values(preset.colors).map((color, idx) => (
                  <div
                    key={idx}
                    className="preset-color-sample"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
              <div className="theme-preset-name">{preset.name}</div>
              <button
                className="theme-preset-btn"
                onClick={() => handlePresetApply(key)}
              >
                Aplicar
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Seção de Cores Customizáveis */}
      <div className="settings-section">
        <div className="colors-section-header">
          <h3 className="settings-section-title">🎯 Cores Customizáveis</h3>
          <button 
            className="reset-colors-btn"
            onClick={handleResetColors}
            title="Restaurar cores padrão"
          >
            ↻ Restaurar Padrão
          </button>
        </div>
        <p className="settings-section-description">Customize até 6 cores diferentes para personalizar completamente sua experiência</p>

        <div className="colors-grid">
          <ColorPickerInput
            label="Cor Primária"
            description="Cor principal da interface"
            value={colors.primary}
            onChange={(value) => handleColorChange("primary", value)}
            colorKey="primary"
          />
          <ColorPickerInput
            label="Cor Secundária"
            description="Cor secundária e elementos auxiliares"
            value={colors.secondary}
            onChange={(value) => handleColorChange("secondary", value)}
            colorKey="secondary"
          />
          <ColorPickerInput
            label="Cor Destaque (Accent)"
            description="Cor para elementos em destaque"
            value={colors.accent}
            onChange={(value) => handleColorChange("accent", value)}
            colorKey="accent"
          />
          <ColorPickerInput
            label="Cor Sucesso"
            description="Cor para mensagens de sucesso"
            value={colors.success}
            onChange={(value) => handleColorChange("success", value)}
            colorKey="success"
          />
          <ColorPickerInput
            label="Cor Aviso"
            description="Cor para avisos e alertas"
            value={colors.warning}
            onChange={(value) => handleColorChange("warning", value)}
            colorKey="warning"
          />
          <ColorPickerInput
            label="Cor Perigo"
            description="Cor para erros e ações perigosas"
            value={colors.danger}
            onChange={(value) => handleColorChange("danger", value)}
            colorKey="danger"
          />
        </div>

        {/* Preview das cores */}
        <div className="color-preview-section">
          <h4>Preview das Cores</h4>
          <div className="color-preview-grid">
            <div className="color-preview-item">
              <div 
                className="color-preview-box" 
                style={{ backgroundColor: colors.primary }}
              />
              <div className="color-preview-label">Primária</div>
              <div className="color-preview-value">{colors.primary}</div>
            </div>
            <div className="color-preview-item">
              <div 
                className="color-preview-box" 
                style={{ backgroundColor: colors.secondary }}
              />
              <div className="color-preview-label">Secundária</div>
              <div className="color-preview-value">{colors.secondary}</div>
            </div>
            <div className="color-preview-item">
              <div 
                className="color-preview-box" 
                style={{ backgroundColor: colors.accent }}
              />
              <div className="color-preview-label">Destaque</div>
              <div className="color-preview-value">{colors.accent}</div>
            </div>
            <div className="color-preview-item">
              <div 
                className="color-preview-box" 
                style={{ backgroundColor: colors.success }}
              />
              <div className="color-preview-label">Sucesso</div>
              <div className="color-preview-value">{colors.success}</div>
            </div>
            <div className="color-preview-item">
              <div 
                className="color-preview-box" 
                style={{ backgroundColor: colors.warning }}
              />
              <div className="color-preview-label">Aviso</div>
              <div className="color-preview-value">{colors.warning}</div>
            </div>
            <div className="color-preview-item">
              <div 
                className="color-preview-box" 
                style={{ backgroundColor: colors.danger }}
              />
              <div className="color-preview-label">Perigo</div>
              <div className="color-preview-value">{colors.danger}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Seção de Outras Configurações */}
      <div className="settings-section">
        <h3 className="settings-section-title">⚙️ Outras Configurações</h3>
        
        <div className="settings-item">
          <div className="settings-item-info">
            <div className="settings-item-label">Tamanho da fonte</div>
          </div>
          <div className="settings-item-control">
            <select
              className="settings-select"
              value={localSettings.fontSize || "medium"}
              onChange={(e) => handleUpdate("fontSize", e.target.value)}
            >
              <option value="small">Pequena</option>
              <option value="medium">Média</option>
              <option value="large">Grande</option>
            </select>
          </div>
        </div>

        <div className="settings-item">
          <div className="settings-item-info">
            <div className="settings-item-label">Idioma</div>
          </div>
          <div className="settings-item-control">
            <select
              className="settings-select"
              value={localSettings.language || "pt-BR"}
              onChange={(e) => handleUpdate("language", e.target.value)}
            >
              <option value="pt-BR">Português (Brasil)</option>
              <option value="en-US">English (US)</option>
              <option value="es-ES">Español</option>
            </select>
          </div>
        </div>

        <div className="settings-item">
          <div className="settings-item-info">
            <div className="settings-item-label">Modo compacto</div>
            <div className="settings-item-description">Reduzir espaçamento para mostrar mais conteúdo</div>
          </div>
          <div className="settings-item-control">
            <ToggleSwitch
              checked={localSettings.compactMode === true}
              onChange={(val) => handleUpdate("compactMode", val)}
            />
          </div>
        </div>
      </div>

      {/* Seção de Mídia */}
      <div className="settings-section">
        <h3 className="settings-section-title">🎬 Mídia</h3>

        <div className="settings-item">
          <div className="settings-item-info">
            <div className="settings-item-label">Reprodução automática de vídeos</div>
          </div>
          <div className="settings-item-control">
            <ToggleSwitch
              checked={localSettings.autoplayVideos !== false}
              onChange={(val) => handleUpdate("autoplayVideos", val)}
            />
          </div>
        </div>

        <div className="settings-item">
          <div className="settings-item-info">
            <div className="settings-item-label">Mostrar legendas automaticamente</div>
          </div>
          <div className="settings-item-control">
            <ToggleSwitch
              checked={localSettings.showCaptions !== false}
              onChange={(val) => handleUpdate("showCaptions", val)}
            />
          </div>
        </div>
      </div>

      {/* Seção de Acessibilidade */}
      <div className="settings-section">
        <h3 className="settings-section-title">♿ Acessibilidade Visual</h3>

        <div className="settings-item">
          <div className="settings-item-info">
            <div className="settings-item-label">Reduzir movimento</div>
            <div className="settings-item-description">Desabilitar animações e transições</div>
          </div>
          <div className="settings-item-control">
            <ToggleSwitch
              checked={localSettings.reduceMotion === true}
              onChange={(val) => handleUpdate("reduceMotion", val)}
            />
          </div>
        </div>

        <div className="settings-item">
          <div className="settings-item-info">
            <div className="settings-item-label">Alto contraste</div>
          </div>
          <div className="settings-item-control">
            <ToggleSwitch
              checked={localSettings.highContrast === true}
              onChange={(val) => handleUpdate("highContrast", val)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppearanceSettings;
